import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  TableContainer,
  Paper,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Tooltip,
  Chip,
  Link,
  Snackbar,
  Alert,
} from "@mui/material";
import { Search, Edit, Trash2, Star, ArrowUp, ArrowDown, GripVertical, X, ExternalLink, ShoppingCart, Plus, CheckCircle } from 'lucide-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";
import ProductTools from "../components/ProductTools"
import api from '../utils/api';

const favorisGlobalFilterFn = (row, columnId, filterValue) => {
  const { title = "", ean = "", asin = "", comment = "", status = "" } = row.original;
  const combined = `${title} ${ean} ${asin} ${comment} ${status}`.toLowerCase();
  return combined.includes(filterValue.toLowerCase());
};

// Formatage de date au format dd-MM-yyyy
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const FavorisPage = () => {
  const [favoris, setFavoris] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    comment: "",
    status: "Intéressant",
    countryCode: ""
  });
  const [editingId, setEditingId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Nouveaux états pour le redimensionnement et l'ordre des colonnes
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('favorisColumnOrder');
    return savedOrder ? JSON.parse(savedOrder) : [
      'title',
      'ean',
      'asin',
      'purchasePrice',
      'buyBoxLanded',
      'supplier',
      'countryCode',
      'status',
      'comment',
      'createdAt',
      'tools',
      'product_status',
      'actions'
    ];
  });
  const [columnSizing, setColumnSizing] = useState(() => {
    const savedSizing = localStorage.getItem('favorisColumnSizing');
    if (savedSizing) {
      return JSON.parse(savedSizing);
    }
    // Valeurs par défaut
    return {
      title: 250,
      ean: 150,
      asin: 150,
      purchasePrice: 120,
      buyBoxLanded: 120,
      supplier: 150,
      countryCode: 100,
      status: 150,
      comment: 200,
      createdAt: 150,
      tools: 100,
      product_status: 100,
      actions: 120,
    };
  });
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  
  // État pour la sélection et le survol des lignes
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  
  // État pour les filtres de colonnes
  const [filters, setFilters] = useState({
    purchasePrice: { operator: '>=', value: '' },
    buyBoxLanded: { operator: '>=', value: '' },
    status: { value: '' },
    countryCode: { value: '' }
  });

  // État pour les produits existants
  const [existingProducts, setExistingProducts] = useState({});
  // État pour les notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const fetchFavoris = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/favoris");
      const data = Array.isArray(res.data) ? res.data : [];
      setFavoris(data);
      
      // Vérifier quels favoris existent déjà en tant que produits
      await checkExistingProducts(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFavoris();
  }, []);

  useEffect(() => {
    if (columnOrder.length > 0) {
      localStorage.setItem('favorisColumnOrder', JSON.stringify(columnOrder));
    }
  }, [columnOrder]);

  useEffect(() => {
    if (Object.keys(columnSizing).length > 0) {
      localStorage.setItem('favorisColumnSizing', JSON.stringify(columnSizing));
    }
  }, [columnSizing]);

  const handleOpenDialog = (favori) => {
    setFormData({
      comment: favori.comment || "",
      status: favori.status || "Intéressant",
      countryCode: favori.countryCode || ""
    });
    setEditingId(favori.id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  // Fonction pour réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      comment: "",
      status: "Intéressant",
      countryCode: ""
    });
    setEditingId(null);
    setOpenDialog(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingId) {
        await api.put(`/api/favoris/${editingId}`, formData);
      } else {
        await api.post('/api/favoris', formData);
      }
      resetForm();
      fetchFavoris();
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    }
  };

  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/favoris/${deleteId}`);
      fetchFavoris();
      setOpenDeleteDialog(false);
      setDeleteId(null);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  };

  const columnHelper = createColumnHelper();

  const columns = useMemo(
    () => [
      columnHelper.accessor("title", {
        header: "Titre",
        cell: (info) => info.getValue(),
        enableSorting: true,
        size: columnSizing.title,
        enableResizing: true,
      }),
      columnHelper.accessor("ean", {
        header: "EAN",
        cell: (info) => info.getValue(),
        enableSorting: true,
        size: columnSizing.ean,
        enableResizing: true,
      }),
      columnHelper.accessor("asin", {
        header: "ASIN",
        cell: (info) => info.getValue(),
        enableSorting: true,
        size: columnSizing.asin,
        enableResizing: true,
      }),
      columnHelper.accessor("purchasePrice", {
        header: "Prix d'achat",
        cell: (info) => {
          const value = info.getValue();
          return value ? `${value.toFixed(2)} €` : "-";
        },
        enableSorting: true,
        size: columnSizing.purchasePrice,
        enableResizing: true,
        filterFn: 'custom',
        enableColumnFilter: true,
      }),
      columnHelper.accessor("buyBoxLanded", {
        header: "Buy Box",
        cell: (info) => {
          const value = info.getValue();
          return value ? `${value.toFixed(2)} €` : "-";
        },
        enableSorting: true,
        size: columnSizing.buyBoxLanded,
        enableResizing: true,
        filterFn: 'custom',
        enableColumnFilter: true,
      }),
      columnHelper.accessor("supplier", {
        header: "Fournisseur",
        cell: (info) => info.getValue() || "-",
        enableSorting: true,
        size: columnSizing.supplier,
        enableResizing: true,
      }),
      columnHelper.accessor("countryCode", {
        header: "Pays",
        cell: (info) => {
          const countryCode = info.getValue();
          
          // Si pas de pays spécifique, afficher "Global"
          if (!countryCode) {
            return (
              <Box sx={{ fontStyle: 'italic', color: '#666' }}>
                Global
              </Box>
            );
          }
          
          // Mapping des codes pays vers leurs drapeaux
          const countryFlags = {
            FR: "https://flagcdn.com/24x18/fr.png",
            ES: "https://flagcdn.com/24x18/es.png",
            IT: "https://flagcdn.com/24x18/it.png",
            DE: "https://flagcdn.com/24x18/de.png",
            UK: "https://flagcdn.com/24x18/gb.png",
            NL: "https://flagcdn.com/24x18/nl.png",
            PL: "https://flagcdn.com/24x18/pl.png",
            SE: "https://flagcdn.com/24x18/se.png",
            BE: "https://flagcdn.com/24x18/be.png",
            IE: "https://flagcdn.com/24x18/ie.png"
          };
          
          // Affichage normal avec drapeau pour un pays spécifique
          const flagUrl = countryFlags[countryCode];
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {flagUrl && (
                <img 
                  src={flagUrl} 
                  alt={countryCode} 
                  style={{ width: 24, height: 18, border: '1px solid #eee' }}
                />
              )}
              <span>{countryCode}</span>
            </Box>
          );
        },
        enableSorting: true,
        size: columnSizing.countryCode || 100,
        enableResizing: true,
        filterFn: 'equals',
        enableColumnFilter: true,
      }),
      columnHelper.accessor("status", {
        header: "Statut",
        cell: (info) => {
          const status = info.getValue();
          let color = "default";
          
          switch (status) {
            case "Intéressant":
              color = "success";
              break;
            case "Presque intéressant":
              color = "warning";
              break;
            case "Plus de stock":
              color = "error";
              break;
            case "À surveiller":
              color = "info";
              break;
            default:
              color = "default";
          }
          
          return (
            <Chip 
              label={status} 
              color={color} 
              size="small" 
            />
          );
        },
        enableSorting: true,
        size: columnSizing.status,
        enableResizing: true,
        filterFn: 'equals',
        enableColumnFilter: true,
      }),
      columnHelper.accessor("comment", {
        header: "Commentaire",
        cell: (info) => info.getValue() || "-",
        enableSorting: true,
        size: columnSizing.comment,
        enableResizing: true,
      }),
      columnHelper.accessor("createdAt", {
        header: "Date d'ajout",
        cell: (info) => formatDateDisplay(info.getValue()),
        enableSorting: true,
        size: columnSizing.createdAt,
        enableResizing: true,
      }),
      columnHelper.accessor("asin", {
        id: "tools",
        header: "Outils",
        cell: (info) => (
          <ProductTools asin={info.getValue()} countryCode="FR" />
        ),
        enableSorting: false,
        size: columnSizing.tools,
        enableResizing: true,
      }),
      columnHelper.accessor("column1", {
        header: "URL Produit",
        cell: (info) => {
          const url = info.getValue();
          return url ? (
            <Tooltip title="Voir le produit sur le site fournisseur">
              <IconButton
                size="small"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ShoppingCart size={20} />
              </IconButton>
            </Tooltip>
          ) : "-";
        },
        enableSorting: true,
        size: columnSizing.column1 || 150,
        enableResizing: true,
      }),
      columnHelper.accessor("id", {
        id: "product_status",
        header: "Produits",
        cell: (info) => {
          const favorisId = info.getValue();
          const exists = existingProducts[favorisId];
          
          return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              {exists ? (
                <Tooltip title="Produit déjà dans la base de données">
                  <CheckCircle size={20} color="success" />
                </Tooltip>
              ) : (
                <Tooltip title="Ajouter aux produits">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToProducts(favorisId);
                    }}
                  >
                    <Plus size={20} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
        enableSorting: false,
        size: columnSizing.product_status || 100,
        enableResizing: true,
      }),
      columnHelper.accessor("id", {
        id: "actions",
        header: "Actions",
        cell: (info) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Modifier">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDialog(info.row.original);
                }}
              >
                <Edit size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDeleteDialog(info.getValue());
                }}
              >
                <Trash2 size={20} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        enableSorting: false,
        size: columnSizing.actions,
        enableResizing: true,
      }),
    ],
    [columnSizing, existingProducts]
  );

  const table = useReactTable({
    data: favoris,
    columns,
    state: {
      globalFilter,
      pagination,
      sorting,
      columnOrder,
      columnSizing,
      columnFilters: Object.entries(filters).map(([id, filter]) => ({
        id,
        value: filter
      }))
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnOrderChange: (newOrder) => {
      setColumnOrder(newOrder);
    },
    onColumnSizingChange: (newSizing) => {
      setColumnSizing(newSizing);
      localStorage.setItem('favorisColumnSizing', JSON.stringify(newSizing));
    },
    onColumnFiltersChange: (updater) => {
      if (typeof updater === 'function') {
        const newFilters = updater(
          Object.entries(filters).map(([id, filter]) => ({
            id,
            value: filter
          }))
        );
        
        const updatedFilters = {};
        newFilters.forEach(filter => {
          updatedFilters[filter.id] = filter.value;
        });
        
        setFilters(updatedFilters);
      } else {
        const updatedFilters = {};
        updater.forEach(filter => {
          updatedFilters[filter.id] = filter.value;
        });
        
        setFilters(updatedFilters);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableColumnResizing: true,
    enableColumnOrdering: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 50,
      maxSize: 500,
      size: 150,
    },
    filterFns: {
      custom: (row, columnId, filterValue) => {
        if (!filterValue || !filterValue.value) return true;
        
        const value = parseFloat(row.getValue(columnId));
        if (isNaN(value)) return true;

        const filterValueNum = parseFloat(filterValue.value);
        if (isNaN(filterValueNum)) return true;

        switch (filterValue.operator) {
          case '>=':
            return value >= filterValueNum;
          case '>':
            return value > filterValueNum;
          case '<=':
            return value <= filterValueNum;
          case '<':
            return value < filterValueNum;
          case '=':
            return value === filterValueNum;
          default:
            return true;
        }
      },
      equals: (row, columnId, filterValue) => {
        if (!filterValue || !filterValue.value) return true;
        
        const value = row.getValue(columnId);
        return value === filterValue.value;
      }
    },
    globalFilterFn: favorisGlobalFilterFn,
    autoResetPageIndex: false,
  });

  // Initialiser l'ordre des colonnes
  useEffect(() => {
    if (columns.length > 0 && columnOrder.length === 0) {
      const initialOrder = columns.map(col => col.id);
      setColumnOrder(initialOrder);
    }
  }, [columns, columnOrder]);

  const pageCount = table.getPageCount() || 0;

  // Fonction pour vérifier quels favoris existent déjà comme produits
  const checkExistingProducts = async (favorisData) => {
    try {
      // Charger tous les produits une seule fois
      const response = await api.get('/api/produits');
      const allProducts = response.data;
      
      // Créer un Map pour une recherche plus rapide, uniquement avec les ASIN
      const productsMap = new Map();
      allProducts.forEach(product => {
        if (product.ASIN) productsMap.set(product.ASIN, true);
      });
      
      // Vérifier chaque favori uniquement par ASIN
      const existingMap = {};
      favorisData.forEach(favori => {
        existingMap[favori.id] = productsMap.has(favori.asin);
      });
      
      setExistingProducts(existingMap);
    } catch (error) {
      console.error('Erreur lors de la vérification des produits existants:', error);
    }
  };
  
  // Fonction pour ajouter un favori comme produit
  const handleAddToProducts = async (favorisId) => {
    try {
      const response = await api.post('/api/produits/from-favori', { favorisId });
      
      // Mettre à jour l'état des produits existants
      setExistingProducts(prev => ({
        ...prev,
        [favorisId]: true
      }));
      
      setSnackbar({
        open: true,
        message: "Produit ajouté avec succès",
        severity: "success"
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout du produit:', error);
      
      // Si le produit existe déjà (statut 409)
      if (error.response && error.response.status === 409) {
        setSnackbar({
          open: true,
          message: "Ce produit existe déjà dans la base de données",
          severity: "info"
        });
      } else {
        setSnackbar({
          open: true,
          message: "Erreur lors de l'ajout du produit",
          severity: "error"
        });
      }
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Favoris</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Chip
            icon={<Star size={20} />}
            label={`${favoris.length} favoris`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>

      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <TextField
          label="Recherche globale"
          variant="outlined"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
      </div>

      {loading ? (
        <CircularProgress />
      ) : favoris.length === 0 ? (
        <Typography>Aucun favori trouvé.</Typography>
      ) : (
        <>
          <TableContainer component={Paper}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid #ccc",
                          borderRight: "1px solid #eee",
                          textAlign: "left",
                          cursor: header.column.getCanSort() ? "pointer" : "default",
                          position: "relative",
                          width: header.getSize(),
                          minWidth: 50,
                          userSelect: "none",
                          fontSize: "0.875rem",
                        }}
                      >
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            cursor: 'move',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                          draggable
                          onDragStart={(e) => {
                            setDraggedColumnId(header.column.id);
                            e.currentTarget.style.opacity = '0.5';
                          }}
                          onDragEnd={(e) => {
                            setDraggedColumnId(null);
                            e.currentTarget.style.opacity = '1';
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            if (draggedColumnId && draggedColumnId !== header.column.id) {
                              const newOrder = table.getAllLeafColumns()
                                .map(col => col.id)
                                .filter(id => id !== draggedColumnId);
                              const targetIndex = newOrder.indexOf(header.column.id);
                              newOrder.splice(targetIndex, 0, draggedColumnId);
                              table.setColumnOrder(newOrder);
                            }
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <GripVertical 
                            sx={{ 
                              mr: 0.5, 
                              opacity: 0.2,
                              fontSize: '1rem',
                              '&:hover': { opacity: 0.5 }
                            }}
                          />
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: <ArrowUp size={16} style={{ marginLeft: 4 }} />,
                            desc: <ArrowDown size={16} style={{ marginLeft: 4 }} />,
                          }[header.column.getIsSorted()] ?? null}
                        </Box>
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            height: '100%',
                            width: '4px',
                            cursor: 'col-resize',
                            userSelect: 'none',
                            touchAction: 'none',
                            backgroundColor: '#e0e0e0',
                            opacity: 0.5,
                            transition: 'opacity 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.backgroundColor = '#2196f3';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '0.5';
                            e.currentTarget.style.backgroundColor = '#e0e0e0';
                          }}
                        />
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                <tr>
                  {table.getHeaderGroups()[0].headers.map((header) => {
                    const columnId = header.column.id;
                    const filter = filters[columnId];
                    
                    if (filter) {
                      // Filtres spéciaux pour status et countryCode
                      if (columnId === 'status' || columnId === 'countryCode') {
                        // Récupérer les valeurs uniques pour la colonne
                        const uniqueValues = [...new Set(favoris.map(item => item[columnId]))].filter(Boolean).sort();
                        
                        return (
                          <td
                            key={`filter-${columnId}`}
                            style={{
                              padding: "2px 4px",
                              borderBottom: "1px solid #eee",
                              borderRight: "1px solid #eee",
                              backgroundColor: "#f5f5f5",
                              fontSize: "0.75rem"
                            }}
                          >
                            <FormControl size="small" fullWidth>
                              <Select
                                value={filter.value || ''}
                                onChange={(e) => {
                                  const newFilter = { ...filter, value: e.target.value };
                                  setFilters(prev => ({
                                    ...prev,
                                    [columnId]: newFilter
                                  }));
                                  setTimeout(() => {
                                    header.column.setFilterValue(newFilter);
                                  }, 0);
                                }}
                                size="small"
                                displayEmpty
                                sx={{ height: '28px' }}
                              >
                                <MenuItem value="">Tous</MenuItem>
                                {columnId === 'countryCode' && <MenuItem value={null}>Global</MenuItem>}
                                {uniqueValues.map((value) => (
                                  <MenuItem key={value} value={value}>
                                    {columnId === 'countryCode' ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {value && (
                                          <img 
                                            src={`https://flagcdn.com/16x12/${value.toLowerCase()}.png`} 
                                            alt={value} 
                                            style={{ marginRight: '4px' }}
                                          />
                                        )}
                                        {value}
                                      </Box>
                                    ) : value}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </td>
                        );
                      }
                      
                      // Filtres numériques standard
                      return (
                        <td
                          key={`filter-${columnId}`}
                          style={{
                            padding: "2px 4px",
                            borderBottom: "1px solid #eee",
                            borderRight: "1px solid #eee",
                            backgroundColor: "#f5f5f5",
                            fontSize: "0.75rem"
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <FormControl size="small" sx={{ minWidth: 60 }}>
                              <Select
                                value={filter.operator}
                                onChange={(e) => {
                                  const newFilter = { ...filter, operator: e.target.value };
                                  setFilters(prev => ({
                                    ...prev,
                                    [columnId]: newFilter
                                  }));
                                  setTimeout(() => {
                                    header.column.setFilterValue(newFilter);
                                  }, 0);
                                }}
                                size="small"
                                sx={{ height: '28px' }}
                              >
                                <MenuItem value=">=">≥</MenuItem>
                                <MenuItem value=">">&gt;</MenuItem>
                                <MenuItem value="<=">≤</MenuItem>
                                <MenuItem value="<">&lt;</MenuItem>
                                <MenuItem value="=">=</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              size="small"
                              value={filter.value}
                              onChange={(e) => {
                                const newFilter = { ...filter, value: e.target.value };
                                setFilters(prev => ({
                                  ...prev,
                                  [columnId]: newFilter
                                }));
                                setTimeout(() => {
                                  header.column.setFilterValue(newFilter);
                                }, 0);
                              }}
                              type="number"
                              sx={{ 
                                width: 60,
                                '& .MuiInputBase-root': {
                                  height: '28px'
                                }
                              }}
                            />
                          </Box>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={`filter-${columnId}`}
                        style={{
                          padding: "2px 4px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid #eee",
                          backgroundColor: "#f5f5f5"
                        }}
                      />
                    );
                  })}
                </tr>
                {table.getRowModel().rows.map((row) => (
                  <tr 
                    key={row.id}
                    onClick={() => setSelectedRowId(row.id === selectedRowId ? null : row.id)}
                    onMouseEnter={() => setHoveredRowId(row.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    style={{
                      backgroundColor: row.id === selectedRowId 
                        ? 'rgba(33, 150, 243, 0.15)' 
                        : row.id === hoveredRowId 
                          ? 'rgba(0, 0, 0, 0.04)' 
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid #eee",
                          width: cell.column.getSize(),
                          minWidth: 50,
                          maxWidth: cell.column.getSize(),
                          fontSize: "0.875rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          position: "relative"
                        }}
                      >
                        {cell.column.id === "title" ? (
                          <Tooltip title={cell.getValue()}>
                            <span style={{
                              display: "block",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}>
                              {cell.getValue()}
                            </span>
                          </Tooltip>
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableContainer>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Button
              variant="contained"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  pageIndex: Math.max(prev.pageIndex - 1, 0),
                }))
              }
              disabled={!table.getCanPreviousPage()}
            >
              Précédent
            </Button>
            <span>
              Page {pagination.pageIndex + 1} sur {pageCount}
            </span>
            <Button
              variant="contained"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  pageIndex: prev.pageIndex + 1,
                }))
              }
              disabled={!table.getCanNextPage()}
            >
              Suivant
            </Button>
          </div>
        </>
      )}

      {/* Dialog pour modifier un favori */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Modifier un favori</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="status-label">Statut</InputLabel>
              <Select
                labelId="status-label"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                label="Statut"
              >
                <MenuItem value="Intéressant">Intéressant</MenuItem>
                <MenuItem value="Presque intéressant">Presque intéressant</MenuItem>
                <MenuItem value="Plus de stock">Plus de stock</MenuItem>
                <MenuItem value="À surveiller">À surveiller</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel id="country-label">Pays</InputLabel>
              <Select
                labelId="country-label"
                value={formData.countryCode || ""}
                onChange={(e) =>
                  setFormData({ ...formData, countryCode: e.target.value === "global" ? "" : e.target.value })
                }
                label="Pays"
              >
                <MenuItem value="global">Global (tous les pays)</MenuItem>
                <MenuItem value="FR">France</MenuItem>
                <MenuItem value="DE">Allemagne</MenuItem>
                <MenuItem value="ES">Espagne</MenuItem>
                <MenuItem value="IT">Italie</MenuItem>
                <MenuItem value="UK">Royaume-Uni</MenuItem>
                <MenuItem value="NL">Pays-Bas</MenuItem>
                <MenuItem value="PL">Pologne</MenuItem>
                <MenuItem value="SE">Suède</MenuItem>
                <MenuItem value="BE">Belgique</MenuItem>
                <MenuItem value="IE">Irlande</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Commentaire"
              multiline
              rows={3}
              value={formData.comment}
              onChange={(e) =>
                setFormData({ ...formData, comment: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer ce favori ? Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Annuler</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default FavorisPage; 